import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Row, Col, ListGroup } from 'react-bootstrap';
import Swal from 'sweetalert2';
import apidefinitions from '../../../../core/api/apiDefinitions';
import AddPartModal from './AddPartModal';

interface VehiclePart {
  id: number;
  name: string;
  partNo: string;
  createdBy: string;
  createdAt: string;
  comment?: string;
  partSource?: string;
  partYear?: number;
  partCondition?: string;
  serialNo?: string;
  warrantyPeriod?: number;
  price?: number;
}

interface VehiclePartData {
  status: string;
  statusDescription?: string;
  data: {
    vehicleParts: VehiclePart[];
    total: number;
  };
}

interface CompleteModuleProps {
  show: boolean;
  onHide: () => void;
  serviceId: number;
  onComplete: () => void;
  initialPartName?: string | null;
}

interface FormData {
  serviceDescription: string;
  serviceCost: number;
  serviceStatus: string;
  vehicleParts: VehiclePart[];
}

const CompleteModule: React.FC<CompleteModuleProps> = ({ show, onHide, serviceId, onComplete, initialPartName }) => {
  const [formData, setFormData] = useState<FormData>({
    serviceDescription: 'Routine maintenance',
    serviceCost: 0,
    serviceStatus: 'COMPLETED',
    vehicleParts: initialPartName
      ? [{ id: 0, name: initialPartName, partNo: 'N/A', createdBy: 'system', createdAt: new Date().toISOString(), comment: '', partSource: '', partYear: 0, partCondition: '', serialNo: '', warrantyPeriod: 0, price: 0 }]
      : [],
  });
  const [billPhotos, setBillPhotos] = useState<File[]>([]);
  const [availableParts, setAvailableParts] = useState<VehiclePart[]>([]);
  const [selectedPartIds, setSelectedPartIds] = useState<number[]>(initialPartName ? [0] : []);
  const [isVehiclePartLoading, setIsVehiclePartLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPartModal, setShowPartModal] = useState(false);
  const [currentPart, setCurrentPart] = useState<VehiclePart | null>(null);

  useEffect(() => {
    const fetchVehicleParts = async () => {
      setIsVehiclePartLoading(true);
      try {
        const payload = { filterType: '', filterValue: '', date: '' };
        const response = await apidefinitions.getVehicleParts(1, payload);
        if (response.data.status === 'S1000' && Array.isArray(response.data.data.vehicleParts)) {
          setAvailableParts(response.data.data.vehicleParts);
        } else {
          setErrorMessage(response.data.statusDescription || 'Failed to load vehicle parts');
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage || 'Failed to load vehicle parts',
            customClass: { popup: 'small-swal' },
          });
        }
      } catch (error: any) {
        setErrorMessage(error.response?.data?.statusDescription || 'Failed to load vehicle parts: Server error');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage || 'Failed to load vehicle parts: Server error',
          customClass: { popup: 'small-swal' },
        });
      } finally {
        setIsVehiclePartLoading(false);
      }
    };
    fetchVehicleParts();
  }, [errorMessage]);

  const handlePartSelect = (partId: number) => {
    if (!selectedPartIds.includes(partId)) {
      const selectedPart = availableParts.find((part) => part.id === partId);
      if (selectedPart) {
        setCurrentPart({ ...selectedPart, comment: '', partSource: '', partYear: new Date().getFullYear(), partCondition: '', serialNo: '', warrantyPeriod: 0, price: 0 });
        setShowPartModal(true);
      }
    }
  };

  const handleAddPart = (part: VehiclePart) => {
    setSelectedPartIds([...selectedPartIds, part.id]);
    setFormData((prev) => ({
      ...prev,
      vehicleParts: [...prev.vehicleParts, part],
    }));
    setShowPartModal(false);
    setCurrentPart(null);
  };

  const handleEditPart = (index: number) => {
    setCurrentPart(formData.vehicleParts[index]);
    setShowPartModal(true);
  };

  const handleUpdatePart = (updatedPart: VehiclePart) => {
    setFormData((prev) => {
      const newParts = [...prev.vehicleParts];
      const index = newParts.findIndex((part) => part.id === updatedPart.id);
      if (index !== -1) {
        newParts[index] = updatedPart;
      }
      return { ...prev, vehicleParts: newParts };
    });
    setShowPartModal(false);
    setCurrentPart(null);
  };

  const handleRemovePart = (index: number) => {
    const partIdToRemove = formData.vehicleParts[index].id;
    setSelectedPartIds(selectedPartIds.filter((id) => id !== partIdToRemove));
    setFormData((prev) => ({
      ...prev,
      vehicleParts: prev.vehicleParts.filter((_, i) => i !== index),
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'serviceCost' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setBillPhotos(Array.from(e.target.files));
    }
  };

  const validateForm = () => {
    if (!formData.serviceDescription.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Service description is required.',
        customClass: { popup: 'small-swal' },
      });
      return false;
    }
    if (formData.serviceCost <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Service cost must be a positive number.',
        customClass: { popup: 'small-swal' },
      });
      return false;
    }
    if (billPhotos.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'At least one bill photo is required.',
        customClass: { popup: 'small-swal' },
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const mappedParts = formData.vehicleParts.map((part) => ({
      vehiclePartId: part.id,
      comment: part.comment,
      partSource: part.partSource,
      partYear: part.partYear,
      partCondition: part.partCondition,
      serialNo: part.serialNo,
      warrantyPeriod: part.warrantyPeriod,
      price: part.price,
    }));

    const payloadToSend = {
      ...formData,
      vehicleParts: mappedParts,
    };

    const totalAmount = payloadToSend.serviceCost + mappedParts.reduce((sum, part) => sum + (part.price || 0), 0);

    const isConfirmed = await Swal.fire({
      title: 'Confirm Submission',
      text: `Total Amount calculated as Rs. ${totalAmount.toLocaleString()}. Proceed with submission?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true,
      customClass: { popup: 'small-swal' },
    });

    if (!isConfirmed.isConfirmed) return;

    try {
      const response = await apidefinitions.completeVehicleServiceRequest(serviceId, payloadToSend, billPhotos);
      if (response.data.status === 'S1000') {
        Swal.fire({
          title: 'Completed Successfully',
          text: `Service ID ${serviceId} has been completed.`,
          icon: 'success',
          customClass: { popup: 'small-swal' },
        });
        onComplete();
        onHide();
      } else {
        throw new Error(response.data.statusDescription || 'Failed to complete service');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.statusDescription || error.response?.data?.error || 'Failed to complete service: Server error';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        customClass: { popup: 'small-swal' },
      });
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton className=" text-white">
          <Modal.Title>Complete Vehicle Service</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          <Row>
            <Col md={8}>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Service Description</Form.Label>
                      <Form.Control
                        type="text"
                        name="serviceDescription"
                        value={formData.serviceDescription}
                        onChange={handleChange}
                        placeholder="Enter service description"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Service Cost (Rs.)</Form.Label>
                      <Form.Control
                        type="number"
                        name="serviceCost"
                        value={formData.serviceCost}
                        onChange={handleChange}
                        step="0.01"
                        min="0.01"
                        placeholder="Enter service cost"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Upload Bill Photos</Form.Label>
                  <Form.Control type="file" multiple onChange={handleFileChange}  required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Select Vehicle Parts</Form.Label>
                  <Form.Select
                    onChange={(e) => handlePartSelect(parseInt(e.target.value))}
                    disabled={isVehiclePartLoading}
                  >
                    <option value="">Select a part...</option>
                    {availableParts
                      .filter((part) => !selectedPartIds.includes(part.id))
                      .map((part) => (
                        <option key={part.id} value={part.id}>
                          {part.name} (Part No: {part.partNo})
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Form>
            </Col>
            <Col md={4}>
              <h5 className="fw-bold mb-3">Selected Parts</h5>
              <ListGroup>
                {formData.vehicleParts.length > 0 ? (
                  formData.vehicleParts.map((part, index) => (
                    <ListGroup.Item
                      key={index}
                      action
                      onClick={() => handleEditPart(index)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>{part.name} (Part No: {part.partNo})</span>
                      <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); handleRemovePart(index); }}>
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))
                ) : (
                  <ListGroup.Item>No parts selected</ListGroup.Item>
                )}
              </ListGroup>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="primary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleSubmit} disabled={isVehiclePartLoading}>
            Complete Service
          </Button>
        </Modal.Footer>
      </Modal>
      {showPartModal && currentPart && (
        <AddPartModal
          show={showPartModal}
          onHide={() => setShowPartModal(false)}
          part={currentPart}
          onSave={selectedPartIds.includes(currentPart.id) ? handleUpdatePart : handleAddPart}
          selectedPartIds={selectedPartIds}
        />
      )}
    </>
  );
};

export default CompleteModule;